import { ReactNode, useEffect, useMemo } from 'react';

import { Box, makeStyles, TextField, Typography } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';

import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  AtendimentoReasonInterface,
  fetchAtendimentoReasons,
} from 'store/reducers/atendimentos';

const useStyles = makeStyles(({ palette, spacing, typography }) => ({
  option: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  optionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: spacing(0.5),
  },
  optionLabel: {
    fontSize: typography.pxToRem(10),
    fontWeight: 600,
    padding: spacing(0.2, 0.8),
    borderRadius: 4,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  globalLabel: {
    backgroundColor: '#EBE6FF',
    color: palette.getContrastText('#EBE6FF'),
  },
  equipeLabel: {
    backgroundColor: palette.grey[300],
    color: palette.getContrastText(palette.grey[300]),
  },
  optionDescription: {
    fontSize: typography.pxToRem(12),
    color: palette.text.secondary,
    marginTop: spacing(0.5),
  },
}));

interface AutocompleteAtendimentoReasonProps
  extends Partial<
    AutocompleteProps<AtendimentoReasonInterface, false, false, false>
  > {
  label?: string;
  variant?: 'outlined' | 'standard' | 'filled';
  fullWidth?: boolean;
  onlyUserReasons?: boolean;
  error?: boolean;
  helperText?: ReactNode;
}

export default function AutocompleteAtendimentoReason({
  label = 'Motivo do atendimento',
  variant = 'outlined',
  fullWidth = true,
  onlyUserReasons = false,
  error,
  helperText,
  ...props
}: AutocompleteAtendimentoReasonProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { reasonsAtendimento, loadingReasonsAtendimento } =
    useAppSelector(atendimentosSelector);

  useEffect(() => {
    if (reasonsAtendimento.length === 0) {
      dispatch(fetchAtendimentoReasons({ onlyUserReasons }));
    }
  }, [dispatch, onlyUserReasons, reasonsAtendimento.length]);

  return (
    <Autocomplete
      options={useMemo(
        () =>
          [...reasonsAtendimento].sort((a, b) => {
            const groupA = a.team
              ? `EQUIPE - ${a.team.name}`
              : 'MOTIVOS GLOBAIS';
            const groupB = b.team
              ? `EQUIPE - ${b.team.name}`
              : 'MOTIVOS GLOBAIS';

            if (groupA === groupB) {
              return a.name.localeCompare(b.name);
            }

            if (groupA === 'MOTIVOS GLOBAIS') return -1;
            if (groupB === 'MOTIVOS GLOBAIS') return 1;

            return groupA.localeCompare(groupB);
          }),
        [reasonsAtendimento],
      )}
      getOptionLabel={option => option.name}
      loading={loadingReasonsAtendimento}
      popupIcon={<ExpandMore />}
      renderOption={option => (
        <Box className={classes.option}>
          <Box className={classes.optionHeader}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {option.name}
            </Typography>
            <Typography
              className={`${classes.optionLabel} ${
                option.team ? classes.equipeLabel : classes.globalLabel
              }`}
            >
              {option.team ? `EQUIPE: ${option.team.name}` : 'GLOBAL'}
            </Typography>
          </Box>
          {option.description && (
            <Typography className={classes.optionDescription}>
              {option.description}
            </Typography>
          )}
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder="Buscar motivo..."
          variant={variant}
          fullWidth={fullWidth}
          error={error}
          helperText={helperText}
        />
      )}
      {...props}
    />
  );
}
